import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * @hook useLoading
 * @description Un hook personalizado para gestionar los estados de carga y error.
 * @param {boolean} [initialState=false] - El estado de carga inicial.
 * @returns {{
 *   isLoading: boolean,
 *   error: string | null,
 *   startLoading: () => void,
 *   stopLoading: () => void,
 *   setLoadingError: (errorMessage: string) => void,
 *   clearError: () => void
 * }} - Un objeto con el estado de carga, el estado de error y las funciones para actualizarlos.
 */
export const useLoading = (initialState = false) => {
  const [isLoading, setIsLoading] = useState(initialState);
  const [error, setError] = useState<string | null>(null);

  const startLoading = useCallback(() => {
    setIsLoading(true);
    setError(null);
  }, []);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
  }, []);

  const setLoadingError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    setIsLoading(false);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isLoading,
    error,
    startLoading,
    stopLoading,
    setLoadingError,
    clearError,
  };
};

/**
 * @hook useDebounce
 * @description Un hook personalizado que retrasa la actualización de un valor hasta que ha pasado una cantidad de tiempo especificada sin nuevas actualizaciones.
 * @template T
 * @param {T} value - El valor a estabilizar.
 * @param {number} delay - El retraso en milisegundos.
 * @returns {T} - El valor estabilizado.
 */
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * @hook useForm
 * @description Un hook personalizado para gestionar el estado, la validación y los envíos de formularios.
 * @template T
 * @param {T} initialValues - Los valores iniciales de los campos del formulario.
 * @param {Partial<Record<keyof T, (value: T[keyof T]) => string | null>>} [validators] - Un objeto con funciones de validación para cada campo del formulario.
 * @returns {{
 *   values: T,
 *   errors: Partial<Record<keyof T, string>>,
 *   touched: Partial<Record<keyof T, boolean>>,
 *   setValue: (field: keyof T, value: T[keyof T]) => void,
 *   setValues: React.Dispatch<React.SetStateAction<T>>,
 *   validateForm: () => boolean,
 *   resetForm: () => void,
 *   isFormValid: () => boolean
 * }} - Un objeto con el estado del formulario y las funciones de manejo.
 */
export const useForm = <T extends Record<string, unknown>>(
  initialValues: T,
  validators?: Partial<Record<keyof T, (value: T[keyof T]) => string | null>>
) => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});

  const setValue = useCallback(
    (field: keyof T, value: T[keyof T]) => {
      setValues((prev) => ({ ...prev, [field]: value }));

      // Validar campo si hay validador
      if (validators?.[field]) {
        const validator = validators[field];
        if (validator) {
          const error = validator(value);
          setErrors((prev) => ({ ...prev, [field]: error }));
        }
      }

      // Marcar como tocado
      setTouched((prev) => ({ ...prev, [field]: true }));
    },
    [validators]
  );

  const validateForm = useCallback((): boolean => {
    if (!validators) return true;

    const newErrors: Partial<Record<keyof T, string>> = {};
    let isValid = true;

    Object.keys(validators).forEach((key) => {
      const field = key as keyof T;
      const validator = validators[field];
      if (validator) {
        const error = validator(values[field]);
        if (error) {
          newErrors[field] = error;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [values, validators]);

  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);

  const isFormValid = useCallback((): boolean => {
    return Object.values(errors).every((error) => !error);
  }, [errors]);

  return {
    values,
    errors,
    touched,
    setValue,
    setValues,
    validateForm,
    resetForm,
    isFormValid,
  };
};

/**
 * @hook useLocalStorage
 * @description Un hook personalizado que sincroniza un valor de estado con el localStorage del navegador.
 * @template T
 * @param {string} key - La clave a utilizar para almacenar el valor en localStorage.
 * @param {T} initialValue - El valor inicial a utilizar si no se encuentra ningún valor en localStorage.
 * @returns {[T, (value: T | ((val: T) => T)) => void, () => void]} - Una tupla con el valor almacenado, una función para actualizarlo y una función para eliminarlo.
 */
export const useLocalStorage = <T>(key: string, initialValue: T) => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      } catch (error) {
        console.error(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, storedValue]
  );

  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialValue);
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
    }
  }, [key, initialValue]);

  return [storedValue, setValue, removeValue] as const;
};

/**
 * @hook useClickOutside
 * @description Un hook personalizado que activa una devolución de llamada cuando se detecta un clic fuera de un elemento especificado.
 * @template T - El tipo del elemento al que se adjuntará la ref.
 * @param {() => void} callback - La función a llamar cuando se detecta un clic fuera.
 * @returns {React.RefObject<T>} - Un objeto ref para ser adjuntado al elemento.
 */
export const useClickOutside = <T extends HTMLElement>(
  callback: () => void
) => {
  const ref = useRef<T>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [callback]);

  return ref;
};

/**
 * @hook useKeyPress
 * @description Un hook personalizado que activa una devolución de llamada cuando se presiona una tecla específica.
 * @param {string} targetKey - La tecla a escuchar (por ejemplo, "Enter", "Escape").
 * @param {() => void} callback - La función a llamar cuando se presiona la tecla.
 */
export const useKeyPress = (targetKey: string, callback: () => void) => {
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === targetKey) {
        callback();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [targetKey, callback]);
};

/**
 * @hook useInactivity
 * @description Hook personalizado que detecta inactividad del usuario y muestra un diálogo de confirmación.
 * Si el usuario no interactúa con el sistema durante un tiempo determinado, se muestra un diálogo.
 * Si el usuario acepta o no responde en 1 minuto, se ejecuta la función de logout.
 * @param {number} inactivityTime - Tiempo en milisegundos antes de mostrar el diálogo (por defecto: 15 minutos).
 * @param {number} dialogTimeout - Tiempo en milisegundos antes de cerrar sesión automáticamente cuando el diálogo está abierto (por defecto: 1 minuto).
 * @param {() => void} onLogout - Función que se ejecuta para cerrar sesión.
 * @param {boolean} [enabled=true] - Si está habilitado el detector de inactividad.
 * @returns {{
 *   showDialog: boolean,
 *   handleConfirm: () => void,
 *   handleCancel: () => void
 * }} - Objeto con el estado del diálogo y las funciones de manejo.
 */
export const useInactivity = (
  inactivityTime: number = 10 * 60 * 1000, // 10 minutos por defecto
  dialogTimeout: number = 60 * 1000, // 1 minuto por defecto
  onLogout: () => void,
  enabled = true
) => {
  const [showDialog, setShowDialog] = useState(false);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const dialogTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  // Usar ref para mantener la referencia más reciente de onLogout sin causar re-renders
  const onLogoutRef = useRef(onLogout);

  // Actualizar la ref cuando onLogout cambie
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  // Función para reiniciar el timer de inactividad
  const resetInactivityTimer = useCallback((skipDialogCheck = false) => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    if (!enabled || (!skipDialogCheck && showDialog)) {
      return;
    }

    lastActivityRef.current = Date.now();
    inactivityTimerRef.current = setTimeout(() => {
      setShowDialog(true);
    }, inactivityTime);
  }, [inactivityTime, enabled, showDialog]);

  // Timer del diálogo: se ejecuta cuando el diálogo se muestra
  useEffect(() => {
    if (!showDialog || !enabled) {
      return;
    }

    // Limpiar cualquier timer previo del diálogo
    if (dialogTimerRef.current) {
      clearTimeout(dialogTimerRef.current);
    }

    // Iniciar timer de 1 minuto cuando se muestra el diálogo
    dialogTimerRef.current = setTimeout(() => {
      // Usar la ref para obtener la función más reciente sin causar re-renders
      onLogoutRef.current();
    }, dialogTimeout);

    return () => {
      if (dialogTimerRef.current) {
        clearTimeout(dialogTimerRef.current);
      }
    };
  }, [showDialog, dialogTimeout, enabled]);

  // Detectar actividad del usuario
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'keydown',
    ];

    const handleActivity = () => {
      if (!showDialog) {
        resetInactivityTimer();
      }
    };

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    // Iniciar el timer al montar
    resetInactivityTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [enabled, showDialog, resetInactivityTimer]);

  // Manejar confirmación (cerrar sesión)
  const handleConfirm = useCallback(() => {
    if (dialogTimerRef.current) {
      clearTimeout(dialogTimerRef.current);
    }
    setShowDialog(false);
    onLogout();
  }, [onLogout]);

  // Manejar cancelación (continuar sesión)
  const handleCancel = useCallback(() => {
    if (dialogTimerRef.current) {
      clearTimeout(dialogTimerRef.current);
    }
    setShowDialog(false);
    // Reiniciar el timer de inactividad (skipDialogCheck para forzar el reinicio)
    setTimeout(() => {
      resetInactivityTimer(true);
    }, 0);
  }, [resetInactivityTimer]);

  return {
    showDialog,
    handleConfirm,
    handleCancel,
  };
};

